import { _optionalChain } from '@sentry/utils/esm/buildPolyfills';
import { getCurrentHub, hasTracingEnabled, startTransaction, captureException, flush } from '@sentry/core';
import { tracingContextFromHeaders, logger, objectify, addExceptionMechanism } from '@sentry/utils';

/**
 * Wraps a function on the edge runtime with error and performance monitoring.
 */
function withEdgeWrapping(
  handler,
  options,
) {
  return async function ( ...args) {
    const req = args[0];
    const currentScope = getCurrentHub().getScope();
    const prevSpan = currentScope.getSpan();

    let span;

    if (hasTracingEnabled()) {
      if (prevSpan) {
        span = prevSpan.startChild({
          description: options.spanDescription,
          op: options.spanOp,
          origin: 'auto.function.nextjs',
        });
      } else if (req instanceof Request) {
        const sentryTrace = req.headers.get('sentry-trace') || '';
        const baggage = req.headers.get('baggage');
        const { traceparentData, dynamicSamplingContext, propagationContext } = tracingContextFromHeaders(
          sentryTrace,
          baggage,
        );
        currentScope.setPropagationContext(propagationContext);
        if (traceparentData) {
          (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log(`[Tracing] Continuing trace ${traceparentData.traceId}.`);
        }

        span = startTransaction({
          name: options.spanDescription,
          op: options.spanOp,
          origin: 'auto.ui.nextjs.withEdgeWrapping',
          ...traceparentData,
          metadata: {
            dynamicSamplingContext: traceparentData && !dynamicSamplingContext ? {} : dynamicSamplingContext,
            source: 'route',
          },
        });
      }

      _optionalChain([currentScope, 'optionalAccess', _ => _.setSpan, 'call', _2 => _2(span)]);
    }

    try {
      const handlerResult = await handler.apply(this, args);

      if ((handlerResult ) instanceof Response) {
        _optionalChain([span, 'optionalAccess', _3 => _3.setHttpStatus, 'call', _4 => _4(handlerResult.status)]);
      } else {
        _optionalChain([span, 'optionalAccess', _5 => _5.setStatus, 'call', _6 => _6('ok')]);
      }

      return handlerResult;
    } catch (e) {
      // In case we have a primitive, wrap it in the equivalent wrapper class (string -> String, etc.) so that we can
      // store a seen flag on it.
      const objectifiedErr = objectify(e);

      _optionalChain([span, 'optionalAccess', _7 => _7.setStatus, 'call', _8 => _8('internal_error')]);

      captureException(objectifiedErr, scope => {
        scope.setSpan(span);
        scope.addEventProcessor(event => {
          addExceptionMechanism(event, {
            type: 'instrument',
            handled: false,
            data: {
              function: options.mechanismFunctionName,
            },
          });
          return event;
        });

        return scope;
      });

      throw objectifiedErr;
    } finally {
      _optionalChain([span, 'optionalAccess', _9 => _9.finish, 'call', _10 => _10()]);
      _optionalChain([currentScope, 'optionalAccess', _11 => _11.setSpan, 'call', _12 => _12(prevSpan)]);
      await flush(2000);
    }
  };
}

export { withEdgeWrapping };
//# sourceMappingURL=edgeWrapperUtils.js.map
